<template>

  <div>
    <b-row class="content-header">
      <b-col
        class="content-header-left mb-2 d-flex align-items-center justify-content-between"
        cols="12"
        md="12"
      >
        <h2 class="custom-header-title mb-0">
          Service Description Setups
        </h2>
        <b-button
          v-if="canViewThisAction('create', 'ServiceDescriptionSet')"
          variant="success"
          :to="{ name: 'operation-service-description-setups-create' }"
        >
          <feather-icon
            icon="PlusIcon"
            size="16"
            class="mr-50"
          />
          Create new Service Description List
        </b-button>
      </b-col>
    </b-row>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="4"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} – {{ dataMeta.to }} of {{ totalRows }}</span>
          </b-col>
          <b-col
            cols="12"
            md="8"
            class="d-flex flex-wrap align-items-center justify-content-md-end"
          >
            <a
              href="javascript:void(0)"
              class="nav-link nav-link-search"
              @click="showSearchBar = !showSearchBar"
            >
              <feather-icon
                icon="SearchIcon"
                size="16"
                class="mr-50"
              />
              <span class="align-middle">Search</span>
            </a>
            <b-input-group
              v-if="showSearchBar"
              class="input-group-merge search-input mr-1 mb-1 mb-md-0"
            >
              <b-form-input
                v-model="searchQuery"
                placeholder="Search..."
                autofocus
              />
              <b-input-group-append is-text>
                <feather-icon
                  icon="XIcon"
                  class="cursor-pointer"
                  @click="searchQuery = ''; showSearchBar = !showSearchBar"
                />
              </b-input-group-append>
            </b-input-group>

            <a
              href="javascript:void(0)"
              class="nav-link nav-link-search"
              @click="isFilterSidebarActive = true"
            >
              <feather-icon
                icon="FilterIcon"
                size="16"
                class="mr-50"
              />
              <span class="align-middle">Filter</span>
            </a>
          </b-col>
        </b-row>
      </div>

      <b-table
        class="position-relative min-height-table"
        :items="serviceDescriptionSets"
        responsive
        :fields="tableColumns"
        primary-key="_id"
        show-empty
        empty-text="No matching service description setups found"
      >
        <template #head(checkbox)>
          <b-form-checkbox
            :checked="isAllSelected"
            @change="toggleSelectAll"
          />
        </template>
        <template #cell(checkbox)="data">
          <b-form-checkbox
            v-model="selectedIds"
            :value="data.item._id"
          />
        </template>

        <template #cell(title)="data">
          <span class="text-bold-black">{{ data.item.title }}</span>
        </template>

        <template #cell(description)="data">
          {{ data.item.description || '–' }}
        </template>

        <template #cell(appointmentTypes)="data">
          {{ appointmentTypesLabel(data.item) }}
        </template>

        <template #cell(serviceTypes)="data">
          {{ serviceTypesLabel(data.item) }}
        </template>

        <template #cell(createdAt)="data">
          <span>{{ data.item.createdBy ? data.item.createdBy.name : '–' }}</span><br>
          <small class="text-muted">{{ dateFormatWithTime(data.item.createdAt) }}</small>
        </template>

        <template #cell(status)="data">
          <b-badge
            :variant="`light-${resolveStatusVariant(data.item.status)}`"
            class="text-capitalize"
          >
            {{ data.item.status }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="canViewThisAction('create', 'ServiceDescriptionSet')"
              :to="{ name: 'operation-service-description-setups-create', query: { from: data.item._id } }"
            >
              <span class="align-middle ml-50">Duplicate</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canViewThisAction('update', 'ServiceDescriptionSet')"
              :to="{ name: 'operation-service-description-setups-edit', params: { id: data.item._id } }"
            >
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canViewThisAction('delete', 'ServiceDescriptionSet')"
              @click="deleteEntity(data.item._id)"
            >
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div
        v-if="totalRows > perPage"
        class="mx-2 mb-2"
      >
        <b-row>
          <b-col
            cols="12"
            class="d-flex align-items-center justify-content-center"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              align="center"
              prev-text="Prev"
              next-text="Next"
            />
          </b-col>
        </b-row>
      </div>
    </b-card>

    <service-description-setup-filters
      :is-filter-sidebar-active.sync="isFilterSidebarActive"
      @apply-filter="onApplyFilter"
    />
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BCard, BTable, BBadge, BDropdown, BDropdownItem, BFormCheckbox, BFormInput,
  BInputGroup, BInputGroupAppend, BPagination,
} from 'bootstrap-vue'
import { useUtils as useAclUtils } from '@/libs/acl/custom'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ServiceDescriptionSetupFilters from './ServiceDescriptionSetupFilters.vue'

const { canViewThisAction } = useAclUtils()

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BCard,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BFormCheckbox,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BPagination,
    ServiceDescriptionSetupFilters,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },

  data() {
    return {
      canViewThisAction,
      isFilterSidebarActive: false,
      showSearchBar: false,
      searchQuery: '',
      appliedFilters: null,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      tableColumns: [
        { key: 'checkbox', label: '' },
        { key: 'title', label: 'Setup Name' },
        { key: 'description', label: 'Internal Description' },
        { key: 'appointmentTypes', label: 'Appointment Types' },
        { key: 'serviceTypes', label: 'Service Types' },
        { key: 'createdAt', label: 'Created Date' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: '' },
      ],
      serviceDescriptionSets: [],
      selectedIds: [],
      searchDebounce: null,
    }
  },

  computed: {
    dataMeta() {
      const from = this.totalRows === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1
      const to = Math.min(this.totalRows, this.currentPage * this.perPage)
      return { from, to }
    },
    isAllSelected() {
      return this.serviceDescriptionSets.length > 0 && this.serviceDescriptionSets.every(o => this.selectedIds.includes(o._id))
    },
  },

  watch: {
    searchQuery() {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.resetToFirstPage()
      }, 400)
    },
    currentPage() {
      this.fetchServiceDescriptionSets()
    },
  },

  mounted() {
    this.fetchServiceDescriptionSets()
  },

  methods: {
    resolveStatusVariant(status) {
      if (status === 'Active') return 'success'
      return 'secondary'
    },
    appointmentTypesLabel(item) {
      if (!item.applicableAppointmentTypes || item.applicableAppointmentTypes.length === 0) return 'All'
      return item.applicableAppointmentTypes.map(type => type.typeName).join(', ')
    },
    serviceTypesLabel(item) {
      if (!item.applicableServiceTypes || item.applicableServiceTypes.length === 0) return 'All'
      return item.applicableServiceTypes.map(type => type.name).join(', ')
    },
    onApplyFilter(filters) {
      this.appliedFilters = filters
      this.resetToFirstPage()
    },
    resetToFirstPage() {
      if (this.currentPage === 1) {
        this.fetchServiceDescriptionSets()
      } else {
        this.currentPage = 1
      }
    },
    toggleSelectAll(checked) {
      const visibleIds = this.serviceDescriptionSets.map(o => o._id)
      this.selectedIds = checked
        ? [...new Set([...this.selectedIds, ...visibleIds])]
        : this.selectedIds.filter(id => !visibleIds.includes(id))
    },
    fetchServiceDescriptionSets() {
      const params = {
        perPage: this.perPage,
        page: this.currentPage,
        sortBy: 'createdAt',
        sortDesc: true,
      }

      if (this.searchQuery) params.search = this.searchQuery

      if (this.appliedFilters) {
        if (this.appliedFilters.appointmentTypes && this.appliedFilters.appointmentTypes.length) {
          params.appointmentTypes = this.appliedFilters.appointmentTypes.join(',')
        }
        if (this.appliedFilters.serviceTypes && this.appliedFilters.serviceTypes.length) {
          params.serviceTypes = this.appliedFilters.serviceTypes.join(',')
        }
        if (this.appliedFilters.status) {
          params.status = this.appliedFilters.status
        }
      }

      this.$http.get('operation/service-description-setups', { params })
        .then(response => {
          this.serviceDescriptionSets = response.data.serviceDescriptionSets
          this.totalRows = response.data.total
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    deleteEntity(id) {
      this.$swal({
        title: 'Are You Sure?',
        html: 'Selecting Delete will <strong>permanently delete this Service Description Setup</strong>. This action cannot be undone.',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/warning.png'),
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'No, Cancel',
        confirmButtonText: 'Yes, Delete!',
        customClass: {
          confirmButton: 'btn btn-danger ml-1',
          cancelButton: 'btn btn-outline-primary',
        },
        buttonsStyling: false,
      })
        .then(result => {
          if (result.value) {
            this.$http.delete(`operation/service-description-setups/${id}/delete`)
              .then(() => {
                this.selectedIds = this.selectedIds.filter(selectedId => selectedId !== id)
                this.fetchServiceDescriptionSets()
              })
              .catch(error => {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              })
          }
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.search-input {
  max-width: 220px;
}
</style>
